import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Send,
} from '@mui/icons-material';
import assessmentService, { Assessment, AssessmentAttempt, AssessmentSubmission } from '../../services/assessmentService';
import { format } from 'date-fns';

export type AssessmentSubmitFn = (attemptId: number, submission: AssessmentSubmission) => Promise<AssessmentAttempt>;

interface AssessmentRunnerProps {
  assessment: Assessment;
  attempt: AssessmentAttempt;
  onComplete: () => void;
  /** When provided (e.g. guest flow), used instead of assessmentService.submitAssessment */
  onSubmitSubmission?: AssessmentSubmitFn;
}

const AssessmentRunner: React.FC<AssessmentRunnerProps> = ({
  assessment,
  attempt,
  onComplete,
  onSubmitSubmission,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<AssessmentAttempt | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // Timer logic
  useEffect(() => {
    if (!assessment.durationMinutes || submitted) return;

    const startTime = new Date(attempt.startedAt).getTime();
    const durationMs = assessment.durationMinutes * 60 * 1000;
    const endTime = startTime + durationMs;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0) {
        handleAutoSubmit();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [assessment.durationMinutes, attempt.startedAt, submitted]);

  const handleAnswerChange = (questionId: string, answer: any) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < (assessment.questionCount || 0) - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleAutoSubmit = async () => {
    if (submitted || submitting) return;
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (submitting || submitted) return;

    setSubmitting(true);
    try {
      const submission: AssessmentSubmission = {
        answers,
      };

      const result = onSubmitSubmission
        ? await onSubmitSubmission(attempt.id, submission)
        : await assessmentService.submitAssessment(attempt.id, submission);
      setResult(result);
      setSubmitted(true);
    } catch (error: any) {
      console.error('Failed to submit assessment:', error);
      alert(error.message || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (submitted && result) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity={result.passed ? 'success' : 'error'}
          sx={{ mb: 3 }}
          icon={<CheckCircle />}
        >
          <Typography variant="h6">
            {result.passed ? 'Assessment Passed!' : 'Assessment Failed'}
          </Typography>
          <Typography variant="body2">
            Score: {result.score?.toFixed(1)}% ({result.correctAnswers}/{result.totalQuestions})
          </Typography>
          {result.passed && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Congratulations! You have successfully completed this assessment.
            </Typography>
          )}
        </Alert>

        <Button variant="contained" fullWidth onClick={onComplete}>
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {assessment.title}
        </Typography>
        {timeRemaining !== null && (
          <Alert severity={timeRemaining < 300 ? 'warning' : 'info'} sx={{ mb: 2 }}>
            Time Remaining: {formatTime(timeRemaining)}
          </Alert>
        )}
        <LinearProgress
          variant="determinate"
          value={((currentQuestionIndex + 1) / (assessment.questionCount || 1)) * 100}
          sx={{ mb: 2 }}
        />
        <Typography variant="body2" color="text.secondary">
          Question {currentQuestionIndex + 1} of {assessment.questionCount || 0}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Question {currentQuestionIndex + 1}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {/* Question content would be loaded from assessment details */}
          Question content will be loaded here...
        </Typography>

        {/* Answer input would go here based on question type */}
        <Alert severity="info">
          Answer input components will be implemented based on question types.
        </Alert>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          Previous
        </Button>

        {currentQuestionIndex === (assessment.questionCount || 0) - 1 ? (
          <Button
            variant="contained"
            endIcon={<Send />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Submit Assessment'}
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AssessmentRunner;
